import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import {
  POLICY_WORKLOADS, REMEDIATION_COLUMNS, SYNC_HEALTH_COLUMNS, SYNC_HEALTH_OK_STATUSES, displayPolicyValue, extraPolicyFields, policyValue
} from './policyReview.js';

const PAGE_SIZE = 25;
const METRICS = {
  defender: ['CriticalVulnerabilities', 'HighVulnerabilities', 'ExposedDeviceCount', 'RemediationTaskCount'],
  oneDrive: ['AccountCount', 'ActiveAccountCount', 'TotalStorageUsedGB', 'AccountsAtOrAbove80Percent'],
  oneDriveSync: ['DeviceCount', 'DevicesWithErrors', 'DevicesWithWarnings', 'TotalErrorCount'],
  exchange: ['TotalSharedMailboxCount', 'DueMailboxCount', 'NeverReviewedCount', 'ReattestationDueCount']
};

const SORT_FIELDS = {
  defender: [{ key: 'ExposedDevices', label: 'Exposed devices' }, { key: 'CVSS', label: 'CVSS' }, { key: 'Name', label: 'CVE' }],
  oneDrive: [{ key: 'StorageUsedGB', label: 'Storage used' }, { key: 'LastActivityDate', label: 'Last activity' }, { key: 'UserDisplayName', label: 'Account' }],
  oneDriveSync: [{ key: 'ErrorCount', label: 'Errors' }, { key: 'LastReportedAtUtc', label: 'Last reported' }, { key: 'DeviceName', label: 'Device' }],
  exchange: [{ key: 'ReviewDue', label: 'Review due' }, { key: 'MailboxName', label: 'Mailbox' }, { key: 'CreatedAtUtc', label: 'Created' }]
};

const readableMetric = name => name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/GB$/, '(GB)');
const detailsText = (row, columns) => JSON.stringify(extraPolicyFields(row, columns), null, 2);
const readableScope = scope => scope.replace(/([a-z])([A-Z0-9])/g, '$1 $2');
const messageText = item => typeof item === 'string' ? item : JSON.stringify(item);

const PolicyRows = ({ rows, columns, kind }) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(SORT_FIELDS[kind]?.[0].key || columns[0].key);
  const [page, setPage] = useState(1);
  const [descending, setDescending] = useState(true);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = term ? rows.filter(row => columns.some(column =>
      displayPolicyValue(policyValue(row, column), column.type).toLowerCase().includes(term))) : rows.slice();
    const factor = descending ? -1 : 1;
    return matching.sort((left, right) => {
      const first = left[sortKey] ?? '';
      const second = right[sortKey] ?? '';
      if (typeof first === 'number' && typeof second === 'number') return (first - second) * factor;
      if (typeof first === 'boolean' && typeof second === 'boolean') return (Number(first) - Number(second)) * factor;
      return String(first).localeCompare(String(second), undefined, { numeric: true }) * factor;
    });
  }, [rows, search, sortKey, descending, columns]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <>
      <div className="admin-policy-controls">
        <label><Search size={16} aria-hidden="true" /><input value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} placeholder="Search records" aria-label="Search records" /></label>
        <label>Sort by <select value={sortKey} onChange={event => { setSortKey(event.target.value); setPage(1); }}>
          {(SORT_FIELDS[kind] || columns).map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select></label>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDescending(!descending)} title="Reverse sort order" aria-label="Reverse sort order">{descending ? 'Descending' : 'Ascending'}</button>
        <span className="admin-policy-count">{filtered.length.toLocaleString()} of {rows.length.toLocaleString()} records</span>
      </div>
      {filtered.length ? <>
        <div className="admin-jobdiva-table-wrap admin-policy-table-wrap">
          <table className="admin-jobdiva-table admin-policy-table">
            <thead><tr>{columns.map(column => <th key={column.key}>{column.label}</th>)}<th>Details</th></tr></thead>
            <tbody>{visible.map((row, index) => {
              const details = detailsText(row, columns);
              return <tr key={`${row.Name || row.SiteId || row.MailboxId || row.DeviceId || row.id || index}-${index}`}>
                {columns.map(column => <td key={column.key}>{displayPolicyValue(policyValue(row, column), column.type)}</td>)}
                <td>{details !== '{}' && <details><summary>View</summary><pre className="admin-policy-detail">{details.length > 8000 ? `${details.slice(0, 8000)}\n…` : details}</pre></details>}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        <div className="admin-policy-pagination">
          <button type="button" className="btn btn-secondary btn-sm" title="Previous page" aria-label="Previous page" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={16} /></button>
          <span>Page {currentPage} of {pages}</span>
          <button type="button" className="btn btn-secondary btn-sm" title="Next page" aria-label="Next page" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)}><ChevronRight size={16} /></button>
        </div>
      </> : <p>No records match the search.</p>}
    </>
  );
};

const formatMetric = value => value == null ? '—' : typeof value === 'number' ? value.toLocaleString() : String(value);

const SummaryMetrics = ({ names, summary }) => <div className="admin-policy-metrics">
  {names.map(name => <div key={name}><span>{readableMetric(name)}</span><strong>{formatMetric(summary[name])}</strong></div>)}
</div>;

const AllSummaryMetrics = ({ summary }) => Object.keys(summary).length > 0 && <details><summary>All summary metrics</summary><dl>{Object.entries(summary).map(([key, value]) => <div key={key}><dt>{readableMetric(key)}</dt><dd>{Array.isArray(value) ? value.join(', ') : String(value ?? '—')}</dd></div>)}</dl></details>;

const SyncHealthView = ({ sync }) => {
  if (!sync.available) return <p>OneDrive sync health data was not returned by this review.</p>;
  const healthy = SYNC_HEALTH_OK_STATUSES.includes(sync.status.toLowerCase());
  return <>
    <SummaryMetrics names={METRICS.oneDriveSync} summary={sync.summary} />
    <div className="admin-policy-context">
      <span>
        Status: {sync.status || 'Unknown'}
        {sync.source && ` · Source: ${sync.source}`}
        {sync.errorScope && ` · Error scope: ${readableScope(sync.errorScope)}`}
        {` · Exported: ${sync.exportedAt ? new Date(sync.exportedAt).toLocaleString() : '—'}`}
      </span>
      <AllSummaryMetrics summary={sync.summary} />
    </div>
    {sync.errors.length > 0 && <div role="alert" className="admin-policy-errors">Sync health errors: {sync.errors.map(messageText).join('; ')}</div>}
    {sync.warnings.length > 0 && <div className="admin-policy-errors">Sync health warnings: {sync.warnings.map(messageText).join('; ')}</div>}
    {!healthy && !sync.rows.length
      ? <p>Sync health data was not provided by the function for this run (status: {sync.status || 'unknown'}).</p>
      : sync.rows.length
        ? <PolicyRows key="oneDriveSync" kind="oneDriveSync" rows={sync.rows} columns={SYNC_HEALTH_COLUMNS} />
        : <p>No device records returned.</p>}
  </>;
};

const PolicyReviewPanel = ({ data }) => {
  const [workloadKey, setWorkloadKey] = useState('defender');
  const [defenderView, setDefenderView] = useState('vulnerabilities');
  const [oneDriveView, setOneDriveView] = useState('storage');
  const workload = POLICY_WORKLOADS.find(item => item.key === workloadKey);
  const section = data[workloadKey];
  const summary = section.summary;
  const rows = workloadKey === 'defender' && defenderView === 'remediation' ? data.defender.remediation : section.rows;
  const columns = workloadKey === 'defender' && defenderView === 'remediation' ? REMEDIATION_COLUMNS : workload.columns;
  const showSyncHealth = workloadKey === 'oneDrive' && oneDriveView === 'sync';

  return <div className="admin-policy-review">
    <div className="admin-policy-run">
      <strong className={data.status.toLowerCase() === 'success' ? '' : 'admin-policy-run-warning'}>Review {data.status || 'Unknown'}</strong>
      {data.generatedAt && <span>Generated {new Date(data.generatedAt).toLocaleString()}</span>}
      {data.requestId && <span>Request {data.requestId}</span>}
    </div>
    <div className="admin-policy-tabs" role="tablist" aria-label="Policy review workload">
      {POLICY_WORKLOADS.map(item => <button type="button" role="tab" aria-selected={workloadKey === item.key} key={item.key} className={workloadKey === item.key ? 'active' : ''} onClick={() => setWorkloadKey(item.key)}>
        {item.label} <span>{data[item.key].rows.length.toLocaleString()}</span>
      </button>)}
    </div>
    <section role="tabpanel" aria-label={workload.label}>
      {workloadKey === 'oneDrive' && <div className="admin-policy-subtabs">
        <button type="button" className={oneDriveView === 'storage' ? 'active' : ''} onClick={() => setOneDriveView('storage')}>Storage ({section.rows.length.toLocaleString()})</button>
        <button type="button" className={oneDriveView === 'sync' ? 'active' : ''} onClick={() => setOneDriveView('sync')}>Sync Health ({data.oneDrive.syncHealth.rows.length.toLocaleString()})</button>
      </div>}
      {showSyncHealth ? <SyncHealthView sync={data.oneDrive.syncHealth} /> : !section.available ? <p>{workload.label} data was not returned by this review.</p> : <>
        <SummaryMetrics names={METRICS[workloadKey]} summary={summary} />
        <div className="admin-policy-context">
          {workloadKey === 'oneDrive' && section.period && <span>Report period: {section.period}</span>}
          {workloadKey === 'exchange' && <span>Review cadence: {section.cadenceDays ?? '—'} days · Minimum creation age: {section.minimumAgeDays ?? '—'} days</span>}
          {workloadKey === 'defender' && <span>Severity scope: {summary.SeverityScope?.join(', ') || 'All'}</span>}
          <AllSummaryMetrics summary={summary} />
        </div>
        {workloadKey === 'defender' && <>
          {data.defender.errors.length > 0 && <div role="alert" className="admin-policy-errors">Collection errors: {data.defender.errors.map(error => typeof error === 'string' ? error : JSON.stringify(error)).join('; ')}</div>}
          <div className="admin-policy-subtabs">
            <button type="button" className={defenderView === 'vulnerabilities' ? 'active' : ''} onClick={() => setDefenderView('vulnerabilities')}>Vulnerabilities ({section.rows.length.toLocaleString()})</button>
            <button type="button" className={defenderView === 'remediation' ? 'active' : ''} onClick={() => setDefenderView('remediation')}>Remediation ({data.defender.remediation.length.toLocaleString()})</button>
          </div>
        </>}
        {rows.length ? <PolicyRows key={`${workloadKey}-${defenderView}`} kind={workloadKey} rows={rows} columns={columns} /> : <p>No {workloadKey === 'defender' && defenderView === 'remediation' ? 'remediation tasks' : `${workload.singular} records`} returned.</p>}
      </>}
    </section>
    {data.additional.length > 0 && <details className="admin-policy-additional">
      <summary>Additional data ({data.additional.length})</summary>
      {data.additional.map(({ section: source, name, value }) => <div key={`${source}-${name}`}>
        <strong>{source} · {name}</strong>
        <pre className="admin-policy-detail">{String(typeof value === 'object' ? JSON.stringify(value, null, 2) : value).slice(0, 2000)}</pre>
      </div>)}
    </details>}
  </div>;
};

export default PolicyReviewPanel;